<script>
import UserResource from '@/resources/user'
import PaymentMethod from '@/components/PaymentMethod.vue'
import CostBreakdown from '@/components/CostBreakdown.vue'
import UserEditForm from '@/components/UserEditForm.vue'
import moment from 'moment'

export default {
    name: 'AdminUser',
    components: {
        PaymentMethod,
        CostBreakdown,
        UserEditForm
    },
    computed: {
        bookDateFormattedPretty() {
            return moment(this.bookDate).format('dddd, MMMM Do')
        },
        bookDateFormatted() {
            return moment(this.bookDate).format('YYYY-MM-DD')
        },
        bookTimeDateFormattedPretty() {
            if(!this.bookTimeSlotResource) {
                return this.bookDateFormattedPretty
            }
            return moment(this.bookTimeSlotResource.dateTime).format('dddd, MMMM Do \@ h:mm a')
        },
        isSuperUserOrStaff() {
            return this.userResource.get('is_superuser') || this.userResource.get('is_staff')
        }
    },
    data() {
        return {
            userResource: null,
            userCard: null,
            userHistoryReservationResources: [],
            userBalanceDetails: {
                amount_cents: 0,
                detail: []
            },
            loading: true,
            loadingHistory: true,
            hasFormChanges: false,
        }
    },
    methods: {
        async sendForgotEmail() {
            if(!this.userResource || !this.userResource.get('email')) {
                return false
            }

            await UserResource.forgotPassword(this.userResource.get('email'))
            this.addMessage('success', `Sent login link to ${this.userResource.get('email')}`)
        },
        async deleteUser() {
            try {
                await this.userResource.delete()
                this.addMessage('success', `Successfully deleted ${this.userResource}`)
            } catch(e) {
                this.addMessage('danger', `Couldn't delete user!`)
                throw e
            }
        },
        async showHistory() {
            this.loadingHistory = true
            this.$refs.historyModal.show()
            this.userHistoryReservationResources = await this.userResource.getHistory({ pageSize: 50 })
            this.loadingHistory = false
        },
        async markAsPaid() {
            await this.userResource.markAsPaid()
            this.userBalanceDetails = {
                amount_cents: 0,
                detail: []
            }

            this.$root.$bvToast.toast('All outstanding line items have been marked as "Paid".', {
                title: 'Marked as "Paid"',
                variant: 'success'
            })
        },
        goBack() {
            this.$router.go(-1)
        }
    },
    async mounted() {
        try {
            this.userResource = await UserResource.detail(this.$route.params.userId)
            this.userBalanceDetails = await this.userResource.getBalanceDetails()
        } catch (e) {
            this.$root.$bvToast.toast('Could not find user. It is possible this user belongs to another store.', {
                title: 'User Not Found',
                variant: 'danger'
            })
        } finally {
            this.loading = false
        }
    },
}
</script>

<template>
    <div>
        <b-container class="mt-3" fluid v-if="!loading">
            <b-row>
                <b-col cols="12">
                    <b-button-group class="float-right mb-3">
                        <b-button variant="outline-success" disabled v-if="!!userResource.get('subscription_id')">Active Subscription</b-button>
                        <b-button variant="outline-success" disabled v-if="isSuperUserOrStaff">Admin</b-button>
                        <b-dropdown right text="User Options">
                            <b-dropdown-item :disabled="!userResource.get('customer_id')" :href="`https://dashboard.stripe.com/customers/${userResource.get('customer_id')}`" target="_blank">View Customer in Stripe</b-dropdown-item>
                            <!-- <b-dropdown-item :disabled="!userResource.get('subscription_id')" :href="`https://dashboard.stripe.com/subscriptions/${userResource.get('subscription_id')}`" target="_blank">View Subscription in Stripe</b-dropdown-item> -->
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item :to="{ name: 'addSubscription', query: { userId: userResource.id, redirect: `/admin/user/${userResource.id}` } }" disabled>Add Subscription</b-dropdown-item>
                            <b-dropdown-item @click="showHistory">View History</b-dropdown-item>
                            <b-dropdown-item @click="markAsPaid">Mark all outstanding items as paid</b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item v-b-modal.forgot-password :disabled="!userResource.get('email') || isSuperUserOrStaff">Send Forgot Password Link</b-dropdown-item>
                            <b-dropdown-divider></b-dropdown-divider>
                            <b-dropdown-item v-b-modal.delete-user :disabled="isSuperUserOrStaff" variant="danger">Delete User</b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>
                    <h3>
                        <b-button variant="outline-primary" @click="goBack"><b-icon icon="caret-left" />Back</b-button>
                        <span class="ml-3">User Quick View: <strong>{{ userResource }}</strong></span>
                    </h3>
                    <hr />
                    <b-alert variant="danger" show v-if="userBalanceDetails.amount_cents > 0">This user has an outstanding balance of <strong>{{ currencySymbol() }}{{ moneyFormat(userBalanceDetails.amount_cents) }}</strong></b-alert>
                    <messages />
                </b-col>

                <b-col md="4" class="mt-2">
                    <b-card bg-variant="light" title="Outstanding Items" class="mb-3" v-if="userBalanceDetails.amount_cents > 0">
                        <b-table-simple>
                            <b-thead>
                                <b-tr>
                                    <b-th>Item</b-th>
                                    <b-th class="text-right">Amount</b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr v-for="(item, i) in userBalanceDetails.detail" :key="i">
                                    <b-td>{{ item.description }}</b-td>
                                    <b-td class="text-right">{{ currencySymbol() }}{{ moneyFormat(item.amount_cents) }}</b-td>
                                </b-tr>
                            </b-tbody>
                            <b-tbody>
                                <b-tr>
                                    <b-td class="text-right"><strong>Total</strong></b-td>
                                    <b-td class="text-right"><strong>{{ currencySymbol() }}{{ moneyFormat(userBalanceDetails.amount_cents) }}</strong></b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>
                    </b-card>
                    
                    <b-card bg-variant="light" title="Payment Method" class="mb-3">
                        <b-card-text>
                            <payment-method :userResource="userResource" @retrieve="(card) => userCard = card" cols-lg="12"  />
                        </b-card-text>
                    </b-card>
                </b-col>

                <b-col class="mt-2 mb-5">

                    <b-card bg-variant="light" title="User Details" class="mb-5">
                        <user-edit-form :userResource="userResource" :allowUsernameChange="true" />
                    </b-card>

                </b-col>

            </b-row>

            <!-- Modals -->
            <b-modal id="forgot-password" size="md" title="Send Forgot Password" @ok="sendForgotEmail()" no-fade centered>
                <p>Would you like to send a login link to {{ userResource.get('email') }}</p>
            </b-modal>

            <b-modal id="delete-user" size="md" title="Delete User?" @ok="deleteUser()" no-fade centered>
                <p>Are you sure you want to delete <strong>{{ userResource }}</strong> and cancel their subscription? This action is not reversible.</p>
            </b-modal>

            <b-modal id="user-history" :title="`History for ${userResource} (last 50 entries)`" size="lg" ref="historyModal" no-fade centered>
                <p v-if="loadingHistory">Loading History...</p>
                <table v-else class="table table-striped">
                    <thead class="thead-dark">
                        <tr>
                            <th>Date</th>
                            <th>Seat</th>
                        </tr>
                    </thead>
                    <tbody v-if="userHistoryReservationResources.length > 0">
                        <tr v-for="(reservationResource, i) in userHistoryReservationResources" :key="i">
                            <td>{{ reservationResource }}</td>
                            <td>{{ reservationResource.get('calendar_name') }}</td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="2" class="text-center text-muted">{{ userResource }} doesn't have any history</td>
                        </tr>
                    </tbody>
                </table>
            </b-modal>

        </b-container>

        <b-container v-else>
            <div class="text-center text-muted">
                <b-spinner type="grow" label="Loading..." class="mt-5 mb-5" />
            </div>
        </b-container>
    </div>
</template>

<style lang="scss">
</style>